import _remove from 'lodash/remove';
import { Screen } from 'quasar';
import { SEARCH_TYPES } from 'src/services/constants';
import MapService from 'src/services/map/index';

/**
 * Clears the list of Map Places.
 *
 * @param {ActionContext} context
 */
export function clearMapPlaces(context) {
  context.commit('clearMapPlaces');
}

/**
 * Retrieves results from the Map Places Search using the given terms.
 *
 * @param {ActionContext} context
 * @param {String} terms
 */
export async function mapPlacesSearch(context, terms) {
  MapService.onReady(async () => {
    // Get the center of the map to orient the POIs around a given location
    const { lng, lat } = MapService.getCenter();
    const proximity = `${lng},${lat}`;

    context.commit('setMapPlacesSearchLoading', true);
    const results = await context.rootState.app.broker.mapPlacesSearch({
      terms,
      proximity,
    });
    context.commit('setMapPlacesSearchResults', results);
    context.commit('setMapPlacesSearchLoading', false);
  });
}

/**
 * Saves a result to the recent search history.
 *
 * @param {ActionContext} context
 * @param {Object} searchResult
 */
export function saveHistory(context, searchResult) {
  let history = [...context.state.history];

  // Remove an existing result if found
  _remove(history, { key: searchResult.key });

  // Prepend result to history
  history.unshift(searchResult);

  // Limit to 3 items per type
  let resultsOfType = 0;
  history = history.filter((result) => {
    if (result.type === searchResult.type) {
      resultsOfType += 1;
      if (resultsOfType > 3) {
        return false;
      }
    }
    return true;
  });

  context.commit('setHistory', history);
}

/**
 * Sets the "type" of search to use for Universal Search.
 *
 * @param {ActionContext} context
 * @param {String} type
 */
export function setSearchType(context, type) {
  context.commit('setSearchType', type);
  if (type === SEARCH_TYPES.FILTER && !context.rootState.map.listDialogOpen) {
    // Open list dialog if we're in filter mode
    context.dispatch('map/openListDialog', null, { root: true });
  } else if (Screen.gt.sm && type !== SEARCH_TYPES.FILTER && context.rootState.map.listDialogOpen) {
    /**
     * Close list dialog if we're leaving filter mode and the dialog
     * isn't visible at the current viewport size.
     */
    context.dispatch('map/closeListDialog', null, { root: true });
  }
}

/**
 * Sets the items that will appear in the search results.
 *
 * @param {ActionContext} context
 * @param {Array} items
 */
export function setSearchItems(context, items) {
  context.commit('setSearchItems', items);
}

/**
 * Sets the selected external address index.
 *
 * @param {ActionContext} context
 * @param {Number} index
 */
export function setSelectedExternalAddressIndex(context, index) {
  context.commit('setSelectedExternalAddressIndex', index);
}

/**
 * Sets the Universal Search terms.
 *
 * @param {ActionContext} context
 * @param {String} searchTerms
 */
export async function updateSearchTerms(context, searchTerms) {
  const items = await context.rootState.app.broker.performUniversalSearch(searchTerms);
  context.dispatch('setSearchItems', items);
}

/**
 * Sets the page filter terms.
 *
 * @param {ActionContext} context
 * @param {String} filterTerms
 */
export async function updateFilterTerms(context, filterTerms) {
  context.commit('setFilterTerms', filterTerms);
}
